// src/modules/app-views/omnichannel/pages/Init/modals/OmnichannelParametersModal/modals/AddWhatsappModal/Step2.jsx
// *******************************************************************************
// Step 2: Conditional — Meta credentials (official) or QR code scan (unofficial)
// *******************************************************************************
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Dropdown, Flex, Result, Spin, Upload } from 'antd';
import { CameraOutlined, CheckCircleOutlined, DeleteOutlined, QrcodeOutlined, UploadOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimplePassword from 'components/SimpleComponents/SimplePassword';
import SimpleAvatar from 'components/SimpleComponents/SimpleAvatar';
import AvatarCropModal from 'layouts/SharedComponents/AvatarCropModal';
import WebcamCaptureModal from 'layouts/SharedComponents/WebcamCaptureModal';
import appService from 'services/appService';
import omnichannelService from 'modules/app-views/omnichannel/service';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB (Meta limit)
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MIN_RESOLUTION = 192; // Meta minimum: 192x192 pixels
const normalizePhone = p => String(p ?? '').replace(/\D/g, '').replace(/^0+/, '');

// *******************************************************************************
// META CREDENTIALS CONTENT
// Tenant provides: Business Account ID (WABA), Phone Number ID, Access Token.
// Facebook App ID and Webhook Verify Token belong to Itsafunnel (BSP) and
// are configured in the backend environment — not collected here.
// Avatar widget shown only in edit mode (recordId provided).
// *******************************************************************************
const MetaCredentialsContent = ({ isEditing, profilePicture: initialProfilePicture, recordId, displayName, onAvatarUpdate }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // AVATAR STATE — only active when recordId is set (edit mode)
  // ─────────────────────────────────────────────────────────────────────────────
  const uploadRef = useRef(null);
  // Tracks the original HQ file selected by the user (for full-resolution backend upload)
  const profilePictureFileRef = useRef(null);
  const [profilePicture, setProfilePicture] = useState(initialProfilePicture || null);
  const [uploading, setUploading] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [webcamModalOpen, setWebcamModalOpen] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imageFromWebcam, setImageFromWebcam] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // AVATAR HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const validateFile = useCallback(file => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      appService.message({ flag: 'e', text: translateX('invalid_image_type') });
      return false;
    }
    if (file.size > MAX_FILE_BYTES) {
      appService.message({ flag: 'e', text: translateX('file_too_large_max_2mb') });
      return false;
    }
    return true;
  }, []);

  const beforeUpload = useCallback(async file => {
    if (!validateFile(file)) return Upload.LIST_IGNORE;
    const isValid = await validateFileResolution(file);
    if (!isValid) return Upload.LIST_IGNORE;
    setSelectedImageFile(file);
    setImageFromWebcam(false);
    setCropModalOpen(true);
    return false;
  }, [validateFile]);

  // CropModal callback — first call is preview, second is after Firebase upload finishes
  const handleCropConfirm = useCallback(async avatarData => {
    if (!avatarData) return;

    if (avatarData.isUploading) {
      // Show preview immediately; save original HQ file for backend upload
      setProfilePicture(avatarData.previewUrl);
      setCropModalOpen(false);
      setUploading(true);
      if (selectedImageFile) {
        profilePictureFileRef.current = selectedImageFile;
      }
    } else {
      // Firebase upload done — use base64Thumb for display only
      setUploading(false);
      if (avatarData.base64Thumb) {
        setProfilePicture(avatarData.base64Thumb);
      }
      setSelectedImageFile(null);

      if (recordId) {
        try {
          // Read original HQ file for backend (full resolution, not the thumbnail)
          let base64ToSend;
          if (profilePictureFileRef.current) {
            base64ToSend = await fileToBase64(profilePictureFileRef.current);
            profilePictureFileRef.current = null;
          } else if (avatarData.base64Thumb && !avatarData.base64Thumb.startsWith('blob:')) {
            base64ToSend = avatarData.base64Thumb;
          }

          if (base64ToSend) {
            await omnichannelService.updateWhatsappProfile(recordId, { profilePictureBase64: base64ToSend });
            appService.message({ flag: 's', text: 'saved_successfully' });
            // Notify parent to re-fetch sources so the Select shows the updated avatar
            onAvatarUpdate?.();
          }
        } catch {
          appService.message({ flag: 'e', text: 'error_saving' });
        }
      }
    }
  }, [selectedImageFile, recordId, onAvatarUpdate]);

  const handleCropCancel = useCallback(() => {
    setCropModalOpen(false);
    setSelectedImageFile(null);
    setImageFromWebcam(false);
  }, []);

  const handleWebcamCapture = useCallback(async file => {
    setWebcamModalOpen(false);
    if (file && validateFile(file)) {
      const isValid = await validateFileResolution(file);
      if (!isValid) return;
      setSelectedImageFile(file);
      setImageFromWebcam(true);
      setCropModalOpen(true);
    }
  }, [validateFile]);

  const handleRetakePhoto = useCallback(() => {
    setCropModalOpen(false);
    setSelectedImageFile(null);
    setWebcamModalOpen(true);
  }, []);

  const handleRemove = useCallback(async () => {
    setProfilePicture(null);
    if (recordId) {
      try {
        await omnichannelService.updateWhatsappProfile(recordId, { profilePictureBase64: null });
        onAvatarUpdate?.();
      } catch {
        // Silent — removal is best-effort
      }
    }
  }, [recordId, onAvatarUpdate]);

  const menuItems = [
    { key: 'upload', label: translateX('upload_image'), icon: <UploadOutlined /> },
    { key: 'camera', label: translateX('take_photo'), icon: <CameraOutlined /> },
    ...(profilePicture
      ? [
          { type: 'divider' },
          { key: 'remove', label: translateX('remove_avatar'), icon: <DeleteOutlined />, danger: true },
        ]
      : []),
  ];

  const onMenuClick = useCallback(({ key }) => {
    if (key === 'upload') {
      uploadRef.current?.querySelector('input[type="file"]')?.click();
    } else if (key === 'camera') {
      setWebcamModalOpen(true);
    } else if (key === 'remove') {
      handleRemove();
    }
    setDropdownOpen(false);
  }, [handleRemove]);

  return (
    <>
      {/* Avatar widget — only shown in edit mode when recordId is available */}
      {recordId && (
        <Flex justify="center" style={{ marginBottom: 20 }}>
          <div style={{ position: 'relative' }}>
            <SimpleAvatar
              src={profilePicture || undefined}
              name={displayName || 'WA'}
              size={64}
              shape="circle"
              showBorder
              showShadow
              loading={uploading}
            />
            <div
              style={{
                position: 'absolute',
                bottom: -4,
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(0, 0, 0, 0.5)',
                borderRadius: 16,
                padding: '3px 10px',
              }}
            >
              <Dropdown
                menu={{ items: menuItems, onClick: onMenuClick }}
                trigger={['click']}
                placement="bottom"
                open={dropdownOpen}
                onOpenChange={setDropdownOpen}
              >
                <CameraOutlined style={{ fontSize: 16, color: '#fff', cursor: 'pointer' }} />
              </Dropdown>
            </div>
          </div>
        </Flex>
      )}

      <SimpleInput
        name="businessAccountId"
        label="Business Account ID (WABA)"
        placeholder="Ex: 987654321098765"
        required
        maxLength={50}
        autoFocus={!recordId}
      />

      <SimpleInput
        name="phoneNumberId"
        label="Phone Number ID (Meta)"
        placeholder="Ex: 123456789012345"
        required
        maxLength={50}
        disabled={isEditing}
      />

      {isEditing && (
        <Alert
          type="warning"
          showIcon
          title="Token de acesso"
          description="Deixe o campo em branco para manter o token atual. Preencha apenas se quiser substituí-lo."
          style={{ marginBottom: 16 }}
        />
      )}

      <SimplePassword
        name="accessToken"
        label={`Access Token (Meta)${isEditing ? ' — opcional ao editar' : ''}`}
        placeholder={isEditing ? '(não alterado)' : 'EAAxxxxx...'}
        required={!isEditing}
        maxLength={300}
      />

      {/* Hidden upload trigger + modals — only mounted in edit mode */}
      {recordId && (
        <>
          <div ref={uploadRef} style={{ height: 0, overflow: 'hidden' }}>
            <Upload accept="image/*" showUploadList={false} beforeUpload={beforeUpload} disabled={uploading}>
              <button type="button" style={{ display: 'none' }} />
            </Upload>
          </div>

          <AvatarCropModal
            open={cropModalOpen}
            onCancel={handleCropCancel}
            onConfirm={handleCropConfirm}
            imageFile={selectedImageFile}
            onRetakePhoto={imageFromWebcam ? handleRetakePhoto : undefined}
          />

          <WebcamCaptureModal
            open={webcamModalOpen}
            onCancel={() => setWebcamModalOpen(false)}
            onCapture={handleWebcamCapture}
          />
        </>
      )}
    </>
  );
};

// *******************************************************************************
// BAILEYS QR CODE CONTENT
// *******************************************************************************
const BaileysQrContent = ({ instanceId, savedPhoneNumber, onStatusChange, goNext }) => {
  const [qrCode, setQrCode] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [polling, setPolling] = useState(true);
  const [phoneRetries, setPhoneRetries] = useState(0);
  const [mismatchError, setMismatchError] = useState(null);
  const [phoneWarning, setPhoneWarning] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!instanceId) return;

    let active = true;

    const pollQr = async () => {
      try {
        const [data] = await omnichannelService.getBaileysQr(instanceId);
        if (!active) return;

        if (data?.connectionStatus === 'connected') {
          const scanned = normalizePhone(data.connectedPhone);
          const typed = normalizePhone(savedPhoneNumber);

          if (!scanned) {
            setPhoneRetries(prev => {
              const next = prev + 1;
              if (next >= 2) {
                setPhoneWarning(true);
                setPolling(false);
                if (intervalRef.current) clearInterval(intervalRef.current);
                setTimeout(() => {
                  onStatusChange?.('connected');
                  goNext?.();
                }, 1500);
              }
              return next;
            });
            return;
          }

          if (typed && scanned !== typed) {
            setMismatchError({ typed, scanned });
            setConnectionStatus('connecting');
            setQrCode(null);
            setPolling(false);
            if (intervalRef.current) clearInterval(intervalRef.current);
            return;
          }

          setConnectionStatus('connected');
          onStatusChange?.('connected');
          setQrCode(null);
          setPolling(false);
          if (intervalRef.current) clearInterval(intervalRef.current);
          setTimeout(() => goNext?.(), 1500);
          return;
        }

        if (data?.qrCode) {
          setPhoneRetries(0);
          setMismatchError(null);
          const qr = data.qrCode.startsWith('data:')
            ? data.qrCode
            : `data:image/png;base64,${data.qrCode}`;
          setQrCode(qr);
        }

        setConnectionStatus(data?.connectionStatus || 'connecting');
      } catch {
        // Silently ignore polling errors
      }
    };

    pollQr();
    intervalRef.current = setInterval(pollQr, 3000);

    const timeout = setTimeout(() => {
      if (active) {
        setPolling(false);
        if (intervalRef.current) clearInterval(intervalRef.current);
      }
    }, 120000);

    return () => {
      active = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
      clearTimeout(timeout);
    };
  }, [instanceId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (connectionStatus === 'connected') {
    return (
      <Result
        icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
        title="WhatsApp conectado!"
        subTitle="A instância está ativa e pronta para receber mensagens."
      />
    );
  }

  return (
    <Flex vertical align="center" gap={16} style={{ padding: '16px 0' }}>
      <QrcodeOutlined style={{ fontSize: 24, color: '#25D366' }} />
      <span style={{ fontWeight: 500, fontSize: 14 }}>
        Abra o WhatsApp no celular e escaneie o QR code abaixo
      </span>

      {qrCode ? (
        <img
          src={qrCode}
          alt="QR Code"
          style={{ width: 260, height: 260, borderRadius: 8, border: '1px solid #e0e0e0' }}
        />
      ) : (
        <Flex align="center" justify="center" style={{ width: 260, height: 260 }}>
          <Spin description={translateX('generating_qr_code')} size="large">
            <div style={{ width: 260, height: 200 }} />
          </Spin>
        </Flex>
      )}

      {polling && (
        <span style={{ fontSize: 12, color: '#999' }}>
          Aguardando leitura do QR code... (atualiza automaticamente)
        </span>
      )}

      {!polling && connectionStatus !== 'connected' && !phoneWarning && (
        <span style={{ fontSize: 12, color: '#ff4d4f' }}>
          Tempo expirado. Feche e tente novamente.
        </span>
      )}

      {mismatchError && (
        <Alert
          type="error"
          showIcon
          message={`Número incorreto. Escaneie com o celular do número ${mismatchError.typed}.`}
          style={{ width: '100%' }}
        />
      )}

      {phoneWarning && (
        <Alert
          type="warning"
          showIcon
          title="Não foi possível verificar o número. Prosseguindo..."
          style={{ width: '100%' }}
        />
      )}
    </Flex>
  );
};

// *******************************************************************************
// STEP BUILDER
// *******************************************************************************
const step2 = ({ source, instanceId, savedPhoneNumber, onStatusChange, isEditing = false, profilePicture, recordId, displayName, onAvatarUpdate } = {}) => ({
  stepTitle: source === 'meta' ? 'credentials' : 'QR Code',
  stepTooltip: source === 'meta' ? 'whatsapp_meta_credentials' : 'whatsapp_qr_scan',
  content: ({ goNext }) =>
    source === 'baileys' ? (
      <BaileysQrContent instanceId={instanceId} savedPhoneNumber={savedPhoneNumber} onStatusChange={onStatusChange} goNext={goNext} />
    ) : (
      <MetaCredentialsContent
        isEditing={isEditing}
        profilePicture={profilePicture}
        recordId={recordId}
        displayName={displayName}
        onAvatarUpdate={onAvatarUpdate}
      />
    ),
  validationFields:
    source === 'meta'
      ? isEditing
        ? ['businessAccountId', 'phoneNumberId']
        : ['businessAccountId', 'phoneNumberId', 'accessToken']
      : [],
  // Baileys: hide the Next button — step3 auto-advances after QR scan
  ...(source === 'baileys' ? { nextButtonProps: { style: { display: 'none' } } } : {}),
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export { MetaCredentialsContent, BaileysQrContent };
export default step2;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Read a File object as base64 data URI (preserves original resolution for Meta upload)
 */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Validate a File's image resolution meets Meta minimum (192x192).
 * Shows notification error if too small.
 *
 * @param {File} file - Image file from upload input
 * @returns {Promise<boolean>} true if valid
 */
function validateFileResolution(file) {
  return new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      if (img.width < MIN_RESOLUTION || img.height < MIN_RESOLUTION) {
        appService.notification({
          flag: 'e',
          title: translateX('invalid_resolution'),
          description: `A imagem precisa ter no mínimo ${MIN_RESOLUTION}x${MIN_RESOLUTION} pixels. A imagem selecionada tem ${img.width}x${img.height} pixels.`,
        });
        resolve(false);
      } else {
        resolve(true);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(true); // allow on error — let backend handle it
    };
    img.src = url;
  });
}
